import type { JSX } from 'react';
import type { RefrigerationUnit } from '@/data/schemas';
import { DataList, Panel } from '@/components/blocks/data-display';

/** Simple catalog option for form usage */
type SimpleCatalogOption = {
  id: string;
  label: string;
  description?: string;
  isActive?: boolean;
};
import { resolveFieldValueLabel } from '@/features/catalogs/utils/resolveFieldValue';
import { formatIsoDateDDMMYYYY, formatMonthYear } from '@/utils/date';

/**
 * Normalize text for display, returning "—" for empty/null values
 */
function normalizeText(value: string | null | undefined): string {
  if (!value) return '—';
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : '—';
}

/**
 * Format a number with optional unit suffix
 */
function formatNumber(value: number | null | undefined, unit?: string): string {
  if (value === null || value === undefined) return '—';
  return unit ? `${value} ${unit}` : String(value);
}

export interface RefrigerationUnitAnagraficaProps {
  /** Refrigeration unit data to display */
  unit: RefrigerationUnit | null | undefined;
  /** Site name for context display */
  siteName?: string;
  /** Catalog options for plant location */
  plantLocationOptions?: SimpleCatalogOption[];
  /** Catalog options for toxic gas type */
  toxicGasOptions?: SimpleCatalogOption[];
  /** Catalog options for plant type */
  plantTypeOptions?: SimpleCatalogOption[];
}

/**
 * Encapsulated anagrafica component for refrigeration unit details.
 * Handles catalog lookups, nested data, and computed fields.
 */
export function RefrigerationUnitAnagrafica({
  unit,
  siteName,
  plantLocationOptions = [],
  toxicGasOptions = [],
  plantTypeOptions = [],
}: RefrigerationUnitAnagraficaProps): JSX.Element {
  // Compute formatted values
  const plannedPeriodLabel = unit?.plannedPeriod
    ? formatMonthYear(unit.plannedPeriod, 'it-IT')
    : 'Da pianificare';
  const plannedDateLabel = unit?.plannedDate ? formatIsoDateDDMMYYYY(unit.plannedDate) : '—';

  // Resolve catalog values
  const locationLabel = resolveFieldValueLabel(unit?.location, plantLocationOptions) || '—';
  const toxicGasLabel = resolveFieldValueLabel(unit?.toxicGasType, toxicGasOptions) || '—';
  const plantTypeLabel = resolveFieldValueLabel(unit?.impiantoType, plantTypeOptions) || '—';

  const notes = unit?.technical?.notes?.trim();

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <Panel title="Informazioni generali">
        <DataList
          items={[
            {
              label: 'Nome',
              value: normalizeText(unit?.name),
            },
            {
              label: 'Stabilimento',
              value: normalizeText(siteName),
            },
            {
              label: 'Ubicazione',
              value: locationLabel,
            },
          ]}
        />
      </Panel>
      <Panel title="Parametri tecnici">
        <DataList
          items={[
            {
              label: 'Carica NH₃',
              value: formatNumber(unit?.ammoniaKg, 'kg'),
              mono: true,
            },
            {
              label: 'Gas tossico',
              value: toxicGasLabel,
            },
            {
              label: 'Tipo impianto',
              value: plantTypeLabel,
            },
            {
              label: 'Manutentore incaricato',
              value: unit?.technical?.maintenanceContractor?.name || '—',
            },
          ]}
        />
      </Panel>
      <Panel title="Pianificazione sopralluogo">
        <DataList
          items={[
            {
              label: 'Periodo previsto',
              value: plannedPeriodLabel,
            },
            {
              label: 'Data programmata',
              value: plannedDateLabel,
            },
          ]}
        />
      </Panel>
      <Panel title="Note">
        <DataList
          items={[
            {
              label: 'Note',
              value: notes?.length ? notes : '—',
              layout: 'stacked',
              valueClassName: 'text-sm font-normal text-text-primary whitespace-pre-wrap',
            },
          ]}
        />
      </Panel>
    </div>
  );
}
